import React, {useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import {
	Alert,
	AlertTitle,
	Backdrop,
	Card,
	CardContent,
	CardHeader,
	Chip,
	CircularProgress,
	Collapse,
	IconButton,
	InputAdornment,
	Paper,
	Skeleton,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import AddCircleTwoToneIcon from "@mui/icons-material/AddCircleTwoTone";
import ArrowDownwardTwoToneIcon from "@mui/icons-material/ArrowDownwardTwoTone";
import ArrowUpwardTwoToneIcon from "@mui/icons-material/ArrowUpwardTwoTone";
import FilterListTwoToneIcon from "@mui/icons-material/FilterListTwoTone";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import TuneTwoToneIcon from "@mui/icons-material/TuneTwoTone";
import clsx from "clsx";
import {useHistory} from "react-router-dom";
import {useForm} from "react-hook-form";
import {AnagraficaContext, AppContext, AuthContext, CategoriesContext, TicketContext} from "../../../App";
import {AUTOMATION_INITIAL_STATE, AutomationReducer, initAutomationState} from "../../../reducers/AutomationReducer";
import {getAnagraficaById} from "../../../services/AnagraficaServices";
import {getAllUsersByCompany} from "../../../services/AuthServices";
import {setOpenSnackbar} from "../../../services/AppServices";
import {onClickDetails} from "../../../services/AutomationServices";
import {getAllInfo} from "../../../services/TicketServices";
import {getAccountInfo, getCompanyInfo} from "../../../utils/GenericUtils";
import {useSessionStorage} from "../../../utils/hooks/SessionStorageHook";
import useGetAllTicket from "./utils/useGetAllTicket";
import useTicketSearch from "./utils/useTicketSearch";
import TicketCard from "./details/TicketCard";
import TicketFiltersPanel from "./components/TicketFiltersPanel";
import TicketQueuePanel from "./components/TicketQueuePanel";
import AutocompleteSelect from "../../widgets/inputs/AutocompleteSelect";
import AppPage from "../../widgets/layout/AppPage";
import {TicketStyle} from "../../../styles/pages/tickets/TicketStyle";

const statusLabels = {
	"Nuovo": "Nuovi",
	"Aperto": "Aperti",
	"Preso in carico": "Presi in carico",
	"In attesa": "In attesa",
	"Risolto": "Risolti",
	"Ripristinato": "Ripristinati",
	"Le mie richieste": "Le mie richieste",
	"In gestione": "In gestione",
	"Risposte utente": "Risposte utente",
	"Richieste chiamata": "Richieste chiamata",
};

const sortOptions = [
	{value: "dataCreazione", label: "Data creazione"},
	{value: "dataUltimaModifica", label: "Data ultima modifica"},
	{value: "lastModify", label: "Operatore ultima modifica"},
	{value: "categoria", label: "Categoria"},
	{value: "sottoCategoria", label: "Sottocategoria"},
];

const readPlainSession = (key, fallback = null) => {
	try {
		return sessionStorage.getItem(key) || fallback;
	} catch (error) {
		return fallback;
	}
};

const writePlainSession = (key, value) => {
	try {
		if (value === null || value === undefined || value === "") {
			sessionStorage.removeItem(key);
		} else {
			sessionStorage.setItem(key, value);
		}
	} catch (error) {
		// Session storage is optional for the UI; ignore private-mode failures.
	}
};

const TicketListSkeleton = ({classes}) => (
	<Card className={classes.ticketCard} elevation={0}>
		<CardHeader
			avatar={<Skeleton variant="circular" height={46} width={46}/>}
			title={<Skeleton width={220}/>}
			subheader={<Skeleton width={320}/>}
			action={<Skeleton width={120}/>}
		/>
		<CardContent>
			<Skeleton height={24}/>
			<Skeleton height={22} width="86%"/>
			<Skeleton height={36} width="62%"/>
		</CardContent>
	</Card>
);

const GestioneTicket = () => {
	const ticketClasses = TicketStyle();
	const history = useHistory();
	const ticketContext = useContext(TicketContext);
	const appContext = useContext(AppContext);
	const authContext = useContext(AuthContext);
	const anagraficaContext = useContext(AnagraficaContext);
	const categories = useContext(CategoriesContext);
	const account = getAccountInfo() || {};
	const roles = account.roles || [];
	const rolesKey = roles.join("|");
	const activeRoleLevel = useMemo(() => {
		const roleSet = new Set(rolesKey.split("|").filter(Boolean));
		if (roleSet.has("ROLE_4")) return 4;
		if (roleSet.has("ROLE_3")) return 3;
		if (roleSet.has("ROLE_2")) return 2;
		if (roleSet.has("ROLE_1")) return 1;
		if (roleSet.has("ROLE_0")) return 0;
		return null;
	}, [rolesKey]);

	const [automationState, automationDispatch] = useReducer(AutomationReducer, AUTOMATION_INITIAL_STATE, initAutomationState);
	const [searchValue, setSearchValue] = useSessionStorage("search_value", "");
	const [status, setStatusState] = useState(readPlainSession("stato", "Nuovo"));
	const [priority, setPriority] = useState("");
	const [pageNumber, setPageNumber] = useState(0);
	const [expandedFilters, setExpandedFilters] = useState(false);
	const [selected, setSelected] = useState({});
	const [sort, setSort] = useSessionStorage("sort_t", "dataCreazione");
	const [dir, setDir] = useSessionStorage("dir_t", "DESC");
	const [level, setLevel] = useSessionStorage("level", null);
	const [category, setCategory] = useSessionStorage("categoria", null);
	const [subCategory, setSubCategory] = useSessionStorage("sottoCategoria", null);
	const [ticketFrom, setTicketFrom] = useSessionStorage("from", null);
	const [ticketSample, setTicketSample] = useSessionStorage("ticketSample", {});
	const [esito, setEsitoState] = useState(readPlainSession("esito", null));
	const [openBackdrop, setOpenBackdrop] = useState(false);
	const observer = useRef();
	const queuePanelRef = useRef(null);
	const [ticketWorkspaceHeight, setTicketWorkspaceHeight] = useState(null);
	const {control, errors, getValues, reset} = useForm({
		mode: "onChange",
		defaultValues: sessionStorage.getItem("ticketSample") ? JSON.parse(sessionStorage.getItem("ticketSample")) : {}
	});

	const canCreateTicket = roles.includes("ROLE_CREATOR") || roles.includes("ROLE_ADMIN");

	const {
		loading,
		error,
		hasMore,
		tickets
	} = useGetAllTicket(
		status,
		priority,
		pageNumber,
		null,
		level,
		sort,
		dir,
		category,
		null,
		ticketFrom,
		ticketSample,
		subCategory,
		null,
		searchValue,
		esito
	);
	const {loadingSearch, errorSearch, hasMoreSearch, ticketSearch} = useTicketSearch(searchValue, pageNumber);
	const isSearchMode = searchValue.trim() !== "";
	const visibleTickets = isSearchMode ? ticketSearch : tickets;
	const queueLabel = statusLabels[status] || status;
	const infoTickets = ticketContext.state.infoTickets || {};

	const listTitle = isSearchMode ? "Risultati ricerca" : `Coda ${queueLabel}`;
	const listSubtitle = isSearchMode
		? `Ricerca per "${searchValue}"`
		: "Stato, priorita e filtri applicati alla vista corrente";

	const setStatus = useCallback((nextStatus) => {
		setPageNumber(0);
		setStatusState(nextStatus);
		writePlainSession("stato", nextStatus);
	}, []);

	const setEsito = useCallback((nextEsito) => {
		setPageNumber(0);
		setEsitoState(nextEsito);
		writePlainSession("esito", nextEsito);
	}, []);

	const handleSearch = useCallback((event) => {
		setPageNumber(0);
		setSearchValue(event.target.value);
	}, [setSearchValue]);

	const handlePriorityChange = useCallback((nextPriority) => {
		setPageNumber(0);
		setPriority(nextPriority);
	}, []);

	const handleCategoryChange = useCallback((nextCategory) => {
		setPageNumber(0);
		setSubCategory(null);
		sessionStorage.removeItem("sottoCategoria");
		setCategory(nextCategory);
	}, [setCategory, setSubCategory]);

	const handleSubCategoryChange = useCallback((nextSubCategory) => {
		setPageNumber(0);
		setSubCategory(nextSubCategory);
	}, [setSubCategory]);

	const handleTicketFromChange = useCallback((nextType) => {
		setPageNumber(0);
		setTicketFrom(nextType);
	}, [setTicketFrom]);

	const handleSortChange = useCallback((nextSort) => {
		setPageNumber(0);
		setSort(nextSort);
	}, [setSort]);

	const handleDirectionToggle = useCallback(() => {
		setPageNumber(0);
		setDir(dir === "DESC" ? "ASC" : "DESC");
	}, [dir, setDir]);

	const handleApplyFilters = useCallback(() => {
		setPageNumber(0);
		setTicketSample(getValues());
	}, [getValues, setTicketSample]);

	const handleClearFilters = useCallback(() => {
		reset({});
		setPageNumber(0);
		setPriority("");
		setCategory(null);
		setSubCategory(null);
		setTicketFrom(null);
		setEsito(null);
		setTicketSample({});
		sessionStorage.removeItem("ticketSample");
		sessionStorage.removeItem("categoria");
		sessionStorage.removeItem("sottoCategoria");
		sessionStorage.removeItem("from");
	}, [reset, setCategory, setEsito, setSubCategory, setTicketFrom, setTicketSample]);

	const handleTicketSelect = useCallback((ticket) => {
		setOpenBackdrop(true);
		setSelected(ticket);
		onClickDetails(ticket.id, "ticket", "modify", automationDispatch, ticket);
	}, [automationDispatch]);

	const lastTicketElementRef = useCallback((node) => {
		const currentLoading = isSearchMode ? loadingSearch : loading;
		const currentHasMore = isSearchMode ? hasMoreSearch : hasMore;
		if (currentLoading) return;
		if (observer.current) observer.current.disconnect();
		observer.current = new IntersectionObserver((entries) => {
			if (entries[0].isIntersecting && currentHasMore) {
				setPageNumber((prevState) => prevState + 1);
			}
		});
		if (node) observer.current.observe(node);
	}, [hasMore, hasMoreSearch, isSearchMode, loading, loadingSearch]);

	useEffect(() => {
		if (activeRoleLevel !== null) {
			setLevel(activeRoleLevel);
		}
	}, [activeRoleLevel, setLevel]);

	useEffect(() => {
		getAllInfo(ticketContext.dispatch, category, null, level);
	}, [ticketContext.dispatch, category, level]);

	useEffect(() => {
		if (!automationState.loadingOnClick && automationState.errorOnClick === "") {
			setOpenBackdrop(false);
			if (automationState.operatorOnClick.replaceAll("ERR_", "").includes(account.alias)) {
				getAnagraficaById(selected.idSoggetto, anagraficaContext.dispatch);
				history.push(`/gestione/tickets/${selected.id}`);
			} else {
				setOpenSnackbar("warning", `Il ticket e al momento gestito da ${automationState.operatorOnClick?.replace("ERR_", "")}. Ritenta e sari piu fortunato :)`, appContext.dispatch);
				setTimeout(() => {
					window.location.reload();
				}, 2000);
			}
		} else if (!automationState.loadingOnClick && automationState.errorOnClick !== "") {
			setOpenBackdrop(false);
			if (!automationState.errorOnClick) {
				setOpenSnackbar("warning", "Ops, sembra che tu non possa accedere ... :(", appContext.dispatch);
			} else if (automationState.errorOnClick?.includes("Ops, il ticket")) {
				window.location.reload();
				setOpenSnackbar("info", automationState.errorOnClick, appContext.dispatch);
			} else {
				setOpenSnackbar("error", automationState.errorOnClick, appContext.dispatch);
			}
		}
	}, [
		account.alias,
		automationState.errorOnClick,
		automationState.loadingOnClick,
		automationState.operatorOnClick,
		selected,
		anagraficaContext.dispatch,
		history,
		appContext.dispatch,
	]);

	useEffect(() => {
		setPageNumber(0);
	}, [status, priority, level, sort, dir, category, subCategory, ticketFrom, ticketSample, esito]);

	useEffect(() => {
		getAllUsersByCompany(getCompanyInfo().id, authContext.dispatch);
	}, [authContext.dispatch]);

	useEffect(() => {
		const queuePanel = queuePanelRef.current;
		if (!queuePanel) return undefined;

		const updateWorkspaceHeight = () => {
			const nextHeight = Math.ceil(queuePanel.getBoundingClientRect().height);
			if (nextHeight > 0) {
				setTicketWorkspaceHeight(`${nextHeight}px`);
			}
		};

		updateWorkspaceHeight();
		window.addEventListener("resize", updateWorkspaceHeight);

		const resizeObserver = window.ResizeObserver ? new ResizeObserver(updateWorkspaceHeight) : null;
		if (resizeObserver) {
			resizeObserver.observe(queuePanel);
		}

		return () => {
			window.removeEventListener("resize", updateWorkspaceHeight);
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
		};
	}, [esito, priority, status]);

	return (
		<React.Fragment>
			<Backdrop open={openBackdrop} className={ticketClasses.backdrop}>
				<CircularProgress/>
			</Backdrop>
			<AppPage
				title="Gestione ticket"
				mainClassName={ticketClasses.pageRoot}
				containerClassName={ticketClasses.pageContainer}
			>
					<div className={ticketClasses.ticketShell}>
						<Paper className={ticketClasses.ticketHero} component="section" elevation={0}>
							<div className={ticketClasses.searchBar}>
								<TextField
									className={ticketClasses.searchField}
									placeholder="Cerca per codice fiscale, categoria, operatore, ID o hashtag"
									value={searchValue}
									onChange={handleSearch}
									autoFocus={true}
									fullWidth={true}
									variant="outlined"
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<SearchTwoToneIcon/>
												</InputAdornment>
											),
										},
									}}
								/>
								<Tooltip title={expandedFilters ? "Nascondi filtri" : "Mostra filtri"}>
									<IconButton
										color="inherit"
										onClick={() => setExpandedFilters((prevState) => !prevState)}
										className={clsx(ticketClasses.filterToggle, {
											[ticketClasses.filterToggleActive]: expandedFilters,
										})}
										size="large"
									>
										{expandedFilters ? <TuneTwoToneIcon/> : <FilterListTwoToneIcon/>}
									</IconButton>
								</Tooltip>
							</div>

							<Collapse in={expandedFilters} timeout="auto" unmountOnExit>
								<TicketFiltersPanel
									classes={ticketClasses}
									control={control}
									errors={errors}
									level={level}
									category={category}
									subCategory={subCategory}
									ticketFrom={ticketFrom}
									categoriesV3={categories.categoriesV3}
									users={authContext.state.users}
									loadingUsers={authContext.state.loadingGetAllUsers}
									roles={roles}
									onLevelChange={setLevel}
									onCategoryChange={handleCategoryChange}
									onSubCategoryChange={handleSubCategoryChange}
									onTicketFromChange={handleTicketFromChange}
									onApply={handleApplyFilters}
									onClear={handleClearFilters}
								/>
							</Collapse>
						</Paper>

						<div
							className={ticketClasses.workspace}
							style={ticketWorkspaceHeight ? {"--ticket-workspace-height": ticketWorkspaceHeight} : undefined}
						>
							<TicketQueuePanel
								ref={queuePanelRef}
								classes={ticketClasses}
								counts={infoTickets}
								status={status}
								priority={priority}
								esito={esito}
								onStatusChange={setStatus}
								onPriorityChange={handlePriorityChange}
								onEsitoChange={setEsito}
								onArchiveClick={() => history.push("/gestione/tickets/archivio")}
							/>

							<Paper className={ticketClasses.listPanel} component="section" elevation={0}>
								<div className={ticketClasses.listHeader}>
									<div className={ticketClasses.listHeading}>
										<Typography component="h2" className={ticketClasses.listTitle}>
											{listTitle}
										</Typography>
										<Typography className={ticketClasses.listSubtitle}>
											{listSubtitle}
										</Typography>
									</div>
									<div className={ticketClasses.listChips}>
										<div className={ticketClasses.listSortControls}>
											<AutocompleteSelect
												className={ticketClasses.listSortSelect}
												disableClearable
												label="Ordina per"
												options={sortOptions}
												size="small"
												value={sort}
												onChange={handleSortChange}
											/>
											<Tooltip title={dir === "DESC" ? "Dal piu recente" : "Dal meno recente"}>
												<IconButton
													color="inherit"
													className={ticketClasses.directionToggle}
													onClick={handleDirectionToggle}
													size="small"
													aria-label={dir === "DESC" ? "Ordine decrescente" : "Ordine crescente"}
												>
													{dir === "DESC" ? <ArrowDownwardTwoToneIcon/> : <ArrowUpwardTwoToneIcon/>}
												</IconButton>
											</Tooltip>
										</div>
										{canCreateTicket && (
											<Tooltip title="Crea ticket">
												<IconButton
													color="primary"
													className={ticketClasses.createButton}
													size="small"
													aria-label="Crea ticket"
													onClick={() => history.push("/gestione/tickets/crea")}
												>
													<AddCircleTwoToneIcon/>
												</IconButton>
											</Tooltip>
										)}
										{priority && <Chip size="small" className={ticketClasses.statusChip} label={`Priorita ${priority}`}/>}
										{esito && <Chip size="small" className={ticketClasses.statusChip} label={`Esito ${esito}`}/>}
									</div>
								</div>

								<div className={ticketClasses.ticketList}>
									{visibleTickets.map((ticket, index) => (
										<Card
											ref={visibleTickets.length === index + 1 ? lastTicketElementRef : undefined}
											key={ticket.id}
											className={ticketClasses.ticketCard}
											elevation={0}
										>
											<TicketCard
												ticket={ticket}
												setValue={setSearchValue}
												handleTicketSelect={handleTicketSelect}
												showStorico={true}
											/>
										</Card>
									))}

									{!isSearchMode && loading && <TicketListSkeleton classes={ticketClasses}/>}
									{isSearchMode && loadingSearch && <TicketListSkeleton classes={ticketClasses}/>}

									{!isSearchMode && !loading && !error && tickets.length === 0 && (
										<Alert severity="info" className={ticketClasses.ticketAlert}>
											<AlertTitle>Nessun ticket in questa coda</AlertTitle>
											La vista selezionata e libera.
										</Alert>
									)}
									{!isSearchMode && error && !loading && (
										<Alert severity="error" className={ticketClasses.ticketAlert}>
											<AlertTitle>Errore</AlertTitle>
											Prova un refresh tra qualche minuto. Se persiste, contatta gli sviluppatori.
										</Alert>
									)}
									{isSearchMode && !loadingSearch && !errorSearch && ticketSearch.length === 0 && (
										<Alert severity="info" className={ticketClasses.ticketAlert}>
											<AlertTitle>Nessun risultato</AlertTitle>
											La ricerca non ha trovato ticket.
										</Alert>
									)}
									{isSearchMode && errorSearch && !loadingSearch && (
										<Alert severity="error" className={ticketClasses.ticketAlert}>
											<AlertTitle>Errore ricerca</AlertTitle>
											Prova un refresh tra qualche minuto. Se persiste, contatta gli sviluppatori.
										</Alert>
									)}
								</div>
							</Paper>
						</div>
					</div>
			</AppPage>
		</React.Fragment>
	);
};

export default React.memo(GestioneTicket);
